import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useT } from '../i18n/useT'
import { isSuppressed } from '../lib/backGuardSignals'

// ============================================================
// BackGuard — gestione tasto indietro Android (PWA/WebView).
// ============================================================
//
// Regole (nell'ordine di priorità):
//  1. Se è aperta una MODAL (qualsiasi `.modal-overlay` nel DOM),
//     pressione indietro → chiude la modal (clic sull'overlay, che
//     tutti i nostri componenti gestiscono via `onClick` con
//     `target === currentTarget`). NON tocchiamo la navigazione.
//
//  2. Se la rotta CORRENTE è `/` (Dashboard) e la pressione back
//     non ha cambiato pathname (quindi è stata consumata la nostra
//     sentinel) → doppio tap per uscire:
//        - 1° tap → toast "premi ancora per uscire" + re-push sentinel
//        - 2° tap entro 2s → lascia propagare → app esce (Android
//          chiude il WebView quando la history arriva al fondo)
//
//  3. Se la rotta è cambiata (es. Tasks → Dashboard tramite back)
//     → NON intercettare: React Router gestisce la navigazione.
//     Re-pushiamo sentinel se siamo finiti su `/` per riattivare
//     la guardia sul prossimo back.
//
// Perché l'implementazione precedente falliva:
//  - sentinel pushata solo all'avvio → dopo navigazione Dashboard→Tasks→Dashboard
//    la history aveva altre entry sopra la sentinel, quindi i back su Dashboard
//    finivano su /attivita invece di mostrare il toast.
//  - flag `ignoreNextPop` impostato anche quando la modal si chiudeva con
//    la X (non via back) → il back successivo veniva "mangiato" silenziosamente.
//  - nessuna gestione per modali NON globali (ConfirmDialog, FeedbackModal,
//    bottom sheet Scadenze) → il toast scattava mentre la modal era aperta.
export default function BackGuard() {
  const t = useT()
  const location = useLocation()
  const [showToast, setShowToast] = useState(false)

  // Pathname "stabile" al momento del render corrente.
  // In popstate confrontiamo con `window.location.pathname` post-pop
  // per capire se la history è cambiata o no.
  const stablePathRef = useRef(location.pathname)

  // Timestamp dell'ultima pressione "exit-attempt" su /
  const lastExitPressRef = useRef(0)

  // Tiene il ref aggiornato quando React Router cambia rotta
  useEffect(() => {
    stablePathRef.current = location.pathname
  }, [location.pathname])

  // Sentinel entry: pushata solo mentre siamo su `/`.
  // Quando entri in / → push; quando lasci / → lasciamo che la naturale
  // navigazione consumi / sovrascriva la sentinel. Ogni volta che torni
  // su / ne pushiamo una fresca.
  useEffect(() => {
    if (location.pathname !== '/') return
    window.history.pushState({ backGuard: true }, '', window.location.href)
  }, [location.pathname])

  useEffect(() => {
    const handlePopState = () => {
      // Pop "silenzioso" segnalato da un altro componente (es. cleanup
      // di QuickAddModal che fa history.back() per smontare la sua
      // history entry). Non è una pressione indietro dell'utente.
      if (isSuppressed()) return

      // 1) MODAL APERTE — controllo SINCRONO prima che React possa
      //    unmontare qualcosa. Se c'era una modal al momento del back,
      //    l'utente vuole chiuderla: NON è mai un exit-attempt.
      //
      //    Caso A: modal con popstate listener proprio (QuickAddModal)
      //     → si sta già auto-chiudendo via onClose. Dobbiamo solo
      //     evitare il toast e re-pushare la sentinel.
      //
      //    Caso B: modal senza popstate listener (ConfirmDialog,
      //     FeedbackModal, bottom sheet Scadenze, ecc.)
      //     → al prossimo tick la overlay sarà ancora nel DOM.
      //     La chiudiamo noi clickandola (pattern dismiss standard).
      const overlays = document.querySelectorAll('.modal-overlay')
      if (overlays.length > 0) {
        // Re-push sentinel solo se eravamo già su / (la modal può
        // apparire anche su altre pagine — Profile, Tasks…)
        if (window.location.pathname === '/') {
          window.history.pushState({ backGuard: true }, '', window.location.href)
        }
        // Defer: se la modal auto-chiude (caso A) l'overlay sparirà;
        // altrimenti (caso B) clicchiamo noi per chiuderla.
        setTimeout(() => {
          const stillOpen = document.querySelectorAll('.modal-overlay')
          if (stillOpen.length > 0) {
            stillOpen[stillOpen.length - 1].click()
          }
        }, 0)
        return
      }

      // Nessuna modal → logica route/exit. Usiamo setTimeout per
      // dare priorità a React Router sul re-render, poi leggiamo
      // window.location.pathname che è sempre aggiornato.
      setTimeout(() => {
        const newPath = window.location.pathname
        const oldPath = stablePathRef.current

        // 2) PERCORSO CAMBIATO → navigazione genuina.
        if (newPath !== oldPath) {
          stablePathRef.current = newPath
          return
        }

        // 3) STESSO PATHNAME + su / → exit-attempt double-tap.
        if (newPath === '/') {
          const now = Date.now()
          if (now - lastExitPressRef.current < 2000) {
            return
          }
          lastExitPressRef.current = now
          window.history.pushState({ backGuard: true }, '', window.location.href)
          setShowToast(true)
          setTimeout(() => setShowToast(false), 2000)
          return
        }

        // 4) Stesso pathname fuori da / → caso raro, re-push per
        //    sicurezza ma niente toast.
        window.history.pushState({ backGuard: true }, '', window.location.href)
      }, 0)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, []) // mount once — i ref tengono lo stato dinamico

  if (!showToast) return null
  return <div className="back-toast">{t('back_toast')}</div>
}
